// THRESHOLDS

export const ON_TIME_GRACE_MIN = 6;      // 08:00 + 6 → 08:06
export const LATE_GRACE_MIN = 15;        // 08:00 + 15 → 08:15

export const DEFAULT_WEEKDAY_START = 8 * 60;        // 08:00
export const DEFAULT_WEEKDAY_END   = 16 * 60 + 30;  // 16:30
export const DEFAULT_SATURDAY_START = 8 * 60;       // 08:00
export const DEFAULT_SATURDAY_END   = 13 * 60;      // 13:00


 // TIME HELPERS
export const pad = (n) => String(n).padStart(2, '0');

export const toMinutes = (timeStr) => {
    if (!timeStr) return null;
    const parts = String(timeStr).split(':');
    if (parts.length < 2) return null;
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
};

export const toYMD = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return null;
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

export const todayYMD = () => toYMD(new Date());


// WEEKDAY HELPERS
const dowOf = (ymd) => new Date(`${ymd}T00:00:00`).getDay();
export const isSaturday = (ymd) => dowOf(ymd) === 6;
export const isSunday   = (ymd) => dowOf(ymd) === 0;


 // SHIFT EXTRACTION + RESOLUTION
export const extractShift = (employee) => {
    if (!employee) return null;
    const startTime = employee.shiftStartTime ?? employee.HSD_START_TIME ?? null;
    const endTime   = employee.shiftEndTime   ?? employee.HSD_END_TIME   ?? null;
    const code      = employee.shiftCode      ?? employee.HSD_SHIFT_CODE ?? null;
    const name      = employee.shiftName      ?? employee.HSD_SHIFT_NAME ?? null;
    if (!startTime && !endTime && !code) return null;
    return {
        id: employee.shiftId ?? employee.HED_SHIFT_ID ?? null,
        code,
        name,
        startTime,
        endTime,
    };
};


 // PER-SHIFT ATTENDANCE WINDOWS
export const SHIFT_WINDOWS = {
    /* 08:00 – 16:30 */
    S001: {
        code: 'S001',
        label: 'Week Day (16.30)',
        grace:        { inFrom: '08:00', inTo: '08:05' },
        late:         { inFrom: '08:06', inTo: '08:15' },
        morningShort: { inFrom: '08:06', inTo: '09:30' },
        morningHalf:  { inFrom: '09:31', inTo: '12:30' },
        eveningHalf:  { outFrom: '12:00', outTo: '12:30' },
        eveningShort: { outFrom: '15:00', outTo: '15:30' },
        fullDay:      { start: '08:00', end: '16:30' },
    },
    /* 08:00 – 17:00 */
    S002: {
        code: 'S002',
        label: 'Week Day (17.00)',
        grace:        { inFrom: '08:00', inTo: '08:05' },
        late:         { inFrom: '08:06', inTo: '08:15' },
        morningShort: { inFrom: '08:06', inTo: '09:30' },
        morningHalf:  { inFrom: '09:31', inTo: '12:30' },
        eveningHalf:  { outFrom: '12:00', outTo: '13:00' },
        eveningShort: { outFrom: '15:00', outTo: '15:30' },
        fullDay:      { start: '08:00', end: '17:00' },
    },
};

const compileWindows = (w) => ({
    code: w.code,
    label: w.label,
    startMin:           toMinutes(w.fullDay.start),
    endMin:             toMinutes(w.fullDay.end),
    graceEndMin:        toMinutes(w.grace.inTo),
    lateEndMin:         toMinutes(w.late.inTo),
    morningShortEndMin: toMinutes(w.morningShort.inTo),
    morningHalfEndMin:  toMinutes(w.morningHalf.inTo),
    eveningHalfFromMin: toMinutes(w.eveningHalf.outFrom),
    eveningHalfToMin:   toMinutes(w.eveningHalf.outTo),
    eveningShortFromMin: toMinutes(w.eveningShort.outFrom),
    eveningShortToMin:   toMinutes(w.eveningShort.outTo),
});


 // Rules for a given shift
export function resolveShiftRules(shift) {
    const code = String(shift?.code || '').trim().toUpperCase();
    if (code && SHIFT_WINDOWS[code]) return compileWindows(SHIFT_WINDOWS[code]);

    const name = String(shift?.name || '').trim().toLowerCase();
    if (name) {
        const byName = Object.values(SHIFT_WINDOWS).find(
            (w) => w.label.toLowerCase() === name
        );
        if (byName) return compileWindows(byName);
    }

    const endMin = toMinutes(shift?.endTime);
    if (endMin != null) {
        const byEnd = Object.values(SHIFT_WINDOWS).find(
            (w) => toMinutes(w.fullDay.end) === endMin
        );
        if (byEnd) return compileWindows(byEnd);
    }

    // generic fallback derived from the employee's own shif
    const startMin = toMinutes(shift?.startTime) ?? DEFAULT_WEEKDAY_START;
    const end      = endMin ?? DEFAULT_WEEKDAY_END;
    return {
        code: code || null,
        label: shift?.name || 'Custom shift',
        startMin,
        endMin: end,
        graceEndMin: startMin + 5,
        lateEndMin: startMin + 15,
        morningShortEndMin: 9 * 60 + 30,
        morningHalfEndMin: 12 * 60 + 30,
        eveningHalfFromMin: 12 * 60,
        eveningHalfToMin: Math.max(12 * 60, end - 4 * 60),
        eveningShortFromMin: 15 * 60,
        eveningShortToMin: 15 * 60 + 30,
    };
}

// Kept for backwards compatibility
export function resolveShiftForDate(ymd, shift) {
    if (isSaturday(ymd)) {
        return {
            startMin: DEFAULT_SATURDAY_START,
            endMin: DEFAULT_SATURDAY_END,
            isSaturday: true,
        };
    }
    const r = resolveShiftRules(shift);
    return { startMin: r.startMin, endMin: r.endMin, isSaturday: false };
}

 // MAIN STATUS FUNCTION
const SEVERITY = { present: 0, late: 1, 'short-leave': 2, 'half-day': 3 };

function classifyMorningIn(inMin, rules) {
    if (inMin == null) return { status: 'missing-punch', reason: 'missing-in' };
    if (inMin <= rules.graceEndMin)        return { status: 'present',     reason: 'grace' };
    if (inMin <= rules.lateEndMin)         return { status: 'late',        reason: 'late' };
    if (inMin <= rules.morningShortEndMin) return { status: 'short-leave', reason: 'morning' };
    return { status: 'half-day', reason: 'morning' };
}

function classifyEveningOut(outMin, rules) {
    if (outMin == null) return { status: 'missing-punch', reason: 'missing-out' };
    if (outMin >= rules.endMin) return { status: 'present', reason: 'full-day' };
    if (outMin >= rules.eveningHalfFromMin && outMin <= rules.eveningHalfToMin) {
        return { status: 'half-day', reason: 'evening' };
    }
    if (outMin >= rules.eveningShortFromMin && outMin <= rules.eveningShortToMin) {
        return { status: 'short-leave', reason: 'evening' };
    }
    return { status: 'half-day', reason: 'early-out' };
}

export function computeDayStatus({
    date,
    attendance,
    leave,
    holiday,
    shift,
}) {
    /* ── 1. Attendance row exists ─────────────────────────── */
    if (attendance) {
        const inStr  = attendance.InTime  || '';
        const outStr = attendance.OutTime || '';
        const hasIn  = !!inStr;
        const hasOut = !!outStr;

        if (!hasIn || !hasOut) {
            return {
                status: 'missing-punch',
                missingIn: !hasIn,
                missingOut: !hasOut,
            };
        }

        const inMin  = toMinutes(inStr);
        const outMin = toMinutes(outStr);

        /* ── Saturday: fixed window, only present / late ─── */
        if (isSaturday(date)) {
            const onTimeLimit = DEFAULT_SATURDAY_START + ON_TIME_GRACE_MIN;
            return {
                status: inMin >= onTimeLimit ? 'late' : 'present',
                reason: inMin >= onTimeLimit ? 'late' : 'grace',
                shift: {
                    startMin: DEFAULT_SATURDAY_START,
                    endMin: DEFAULT_SATURDAY_END,
                    isSaturday: true,
                },
            };
        }

        /* ── Weekday: per-shift windows ───────────────────── */
        const rules   = resolveShiftRules(shift);
        const morning = classifyMorningIn(inMin, rules);
        const evening = classifyEveningOut(outMin, rules);

        const winner =
            SEVERITY[evening.status] > SEVERITY[morning.status] ? evening : morning;

        return {
            status: winner.status,
            reason: winner.reason,
            morningStatus: morning.status,
            morningReason: morning.reason,
            eveningStatus: evening.status,
            eveningReason: evening.reason,
            shift: rules,
        };
    }

    /* ── 2. Approved leave ────────────────────────────────── */
    if (leave) return { status: leave.isHalf ? 'half-leave' : 'leave' };

    /* ── 3. Holiday ───────────────────────────────────────── */
    if (holiday) return { status: 'holiday' };

    /* ── 4. Sunday ────────────────────────────────────────── */
    // if (isSunday(date)) return { status: 'sunday' };

    /* ── 5. Nothing ───────────────────────────────────────── */
    return { status: 'no-record' };
}

/* ─────────────────────────────────────────────────────────────
 * 6. HOURS / FORMATTING
 * ────────────────────────────────────────────────────────────*/
export const computeHours = (inTime, outTime) => {
    const inMin = toMinutes(inTime);
    const outMin = toMinutes(outTime);
    if (inMin == null || outMin == null) return 0;
    const diff = outMin - inMin;
    return diff > 0 ? diff / 60 : 0;
};

export const computeOT = (inTime, outTime) => {
    const hrs = computeHours(inTime, outTime);
    return hrs > 8 ? hrs - 8 : 0;
};

export const formatHoursMinutes = (hours) => {
    if (hours == null || hours === '' || Number.isNaN(Number(hours))) return '—';
    const totalMinutes = Math.round(Number(hours) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${String(m).padStart(2, '0')}m`;
};